import { JSON_SPACE } from 'constants/json'
import { RichPlacementMultiAnchorDTO } from 'features/globalSetup/api/types/richPlacementMultiAnchorDTO'
import { RichPlacementMultiAnchor } from 'features/globalSetup/pages/Defaults/RichPlacements/hooks/useGetRichPlacementMultiAnchor'
import { WorkspaceDTO } from 'features/workspace'
import { useWorkspaceParam } from 'features/workspace/hooks/useWorkspaceParam'
import { useQuery } from 'providers/tanstack-query/useQuery'
import { gql } from 'providers/tanstack-query/utils'

export type WorkspaceDefaults = Pick<
  RichPlacementMultiAnchor,
  | 'counterStyle'
  | 'customStyling'
  | 'desktopAdDisplaysBeforeRefresh'
  | 'desktopSingleAdViewTime'
  | 'displayAfterUserInteraction'
  | 'frequencyCappingEnabled'
  | 'fullWidthMode'
  | 'mobileAdDisplaysBeforeRefresh'
  | 'mobileSingleAdViewTime'
  | 'showCloseButton'
>
type Data = {
  workspaceRichPlacementMultianchorConfiguration: Pick<
    RichPlacementMultiAnchorDTO,
    | 'counterStyle'
    | 'customStyling'
    | 'desktopAdDisplaysBeforeRefresh'
    | 'desktopSingleAdViewTime'
    | 'displayAfterUserInteractionEnabled'
    | 'frequencyCappingEnabled'
    | 'fullWidthModeEnabled'
    | 'mobileAdDisplaysBeforeRefresh'
    | 'mobileSingleAdViewTime'
    | 'showCloseButtonEnabled'
  >
}

type Variables = {
  workspaceId: WorkspaceDTO['id']
}

export const useGetWorkspaceDefaults = (): undefined | WorkspaceDefaults => {
  const { workspaceId } = useWorkspaceParam()

  const { data } = useQuery<Variables, Data, WorkspaceDefaults>({
    options: {
      select: ({ workspaceRichPlacementMultianchorConfiguration }: Data): WorkspaceDefaults => {
        const {
          counterStyle,
          customStyling,
          desktopAdDisplaysBeforeRefresh,
          desktopSingleAdViewTime,
          displayAfterUserInteractionEnabled,
          frequencyCappingEnabled,
          fullWidthModeEnabled,
          mobileAdDisplaysBeforeRefresh,
          mobileSingleAdViewTime,
          showCloseButtonEnabled,
        } = workspaceRichPlacementMultianchorConfiguration

        return {
          counterStyle,
          customStyling: JSON.stringify(customStyling, null, JSON_SPACE),
          desktopAdDisplaysBeforeRefresh,
          desktopSingleAdViewTime,
          displayAfterUserInteraction: displayAfterUserInteractionEnabled,
          frequencyCappingEnabled,
          fullWidthMode: fullWidthModeEnabled,
          mobileAdDisplaysBeforeRefresh,
          mobileSingleAdViewTime,
          showCloseButton: showCloseButtonEnabled,
        }
      },
    },
    query: gql`
      query getMultiAnchorWorkspaceDefaults($workspaceId: ID!) {
        workspaceRichPlacementMultianchorConfiguration(workspaceId: $workspaceId) {
          counterStyle
          customStyling
          desktopAdDisplaysBeforeRefresh
          desktopSingleAdViewTime
          displayAfterUserInteractionEnabled
          frequencyCappingEnabled
          fullWidthModeEnabled
          mobileAdDisplaysBeforeRefresh
          mobileSingleAdViewTime
          showCloseButtonEnabled
        }
      }
    `,
    variables: { workspaceId },
  })

  return data
}
